import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Custom hook for intelligent polling with adaptive intervals
 *
 * Polling strategy from FBA Inbound v2024 blueprint:
 * - 0-30 seconds: Poll every 2 seconds
 * - 30+ seconds: Poll every 5 seconds
 *
 * @param {Function} fetchFn - Async function to call on each poll
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether polling is active
 * @param {number} options.initialInterval - Initial polling interval (default: 2000ms)
 * @param {number} options.maxInterval - Max polling interval (default: 5000ms)
 * @param {number} options.intervalThreshold - Time to switch intervals (default: 30000ms)
 * @param {Function} options.onSuccess - Callback on successful poll
 * @param {Function} options.onError - Callback on poll error
 * @param {Function} options.shouldStop - Function to determine if polling should stop
 */
export const usePolling = (fetchFn, options = {}) => {
  const {
    enabled = false,
    initialInterval = 2000,
    maxInterval = 5000,
    intervalThreshold = 30000,
    onSuccess,
    onError,
    shouldStop
  } = options;

  const [data, setData] = useState(null);
  const [status, setStatus] = useState('idle'); // idle, polling, complete, error
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [pollCount, setPollCount] = useState(0);

  const startTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const isMountedRef = useRef(true);

  // Calculate current interval based on elapsed time
  const getCurrentInterval = useCallback(() => {
    return elapsedTime < intervalThreshold ? initialInterval : maxInterval;
  }, [elapsedTime, intervalThreshold, initialInterval, maxInterval]);

  // Execute a single poll
  const executePoll = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      const result = await fetchFn();

      if (!isMountedRef.current) return;

      setData(result);
      setPollCount(prev => prev + 1);

      if (onSuccess) {
        onSuccess(result);
      }

      // Check if we should stop polling
      if (shouldStop && shouldStop(result)) {
        setStatus('complete');
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
          intervalRef.current = null;
        }
        return;
      }

      // Update elapsed time
      if (startTimeRef.current) {
        setElapsedTime(Date.now() - startTimeRef.current);
      }

    } catch (err) {
      if (!isMountedRef.current) return;

      setError(err);
      setStatus('error');

      if (onError) {
        onError(err);
      }

      // Stop polling on error
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [fetchFn, onSuccess, onError, shouldStop]);

  // Schedule next poll
  const scheduleNextPoll = useCallback(() => {
    if (!enabled || status === 'complete' || status === 'error') return;

    const interval = getCurrentInterval();
    intervalRef.current = setTimeout(async () => {
      await executePoll();
      if (isMountedRef.current && status === 'polling') {
        scheduleNextPoll();
      }
    }, interval);
  }, [enabled, status, getCurrentInterval, executePoll]);

  // Start polling
  const startPolling = useCallback(() => {
    setStatus('polling');
    setError(null);
    setElapsedTime(0);
    setPollCount(0);
    startTimeRef.current = Date.now();

    // Execute first poll immediately
    executePoll().then(() => {
      if (isMountedRef.current && status !== 'complete' && status !== 'error') {
        scheduleNextPoll();
      }
    });
  }, [executePoll, scheduleNextPoll, status]);

  // Stop polling
  const stopPolling = useCallback(() => {
    setStatus('idle');
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Reset polling state
  const reset = useCallback(() => {
    stopPolling();
    setData(null);
    setError(null);
    setElapsedTime(0);
    setPollCount(0);
  }, [stopPolling]);

  // Effect to handle enabled state changes
  useEffect(() => {
    if (enabled && status === 'idle') {
      startPolling();
    } else if (!enabled && status === 'polling') {
      stopPolling();
    }
  }, [enabled, status, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, []);

  return {
    data,
    status,
    error,
    elapsedTime,
    pollCount,
    isPolling: status === 'polling',
    isComplete: status === 'complete',
    isError: status === 'error',
    startPolling,
    stopPolling,
    reset,
    currentInterval: getCurrentInterval()
  };
};

/**
 * Hook for polling operation status specifically
 * Used for FBA operations that return an operation_id
 */
export const useOperationPolling = (operationId, options = {}) => {
  const API_URL = import.meta.env.VITE_API_URL;

  const fetchOperationStatus = useCallback(async () => {
    if (!operationId) return null;

    const response = await fetch(`${API_URL}/api/operation_status/${operationId}`);
    if (!response.ok) {
      throw new Error('Failed to fetch operation status');
    }
    return response.json();
  }, [operationId, API_URL]);

  const shouldStop = useCallback((result) => {
    if (!result) return false;
    const status = result.status || result.operation?.status;
    return status === 'SUCCESS' || status === 'FAILED' || status === 'ERROR';
  }, []);

  return usePolling(fetchOperationStatus, {
    ...options,
    enabled: !!operationId && options.enabled !== false,
    shouldStop: options.shouldStop || shouldStop
  });
};

export default usePolling;
